import React from 'react'
import { Link } from 'react-router-dom'
import { ZoomAccordionItem, ZoomResourceLayout } from '../components/zoom'

const SUPPORT_EMAIL = 'service@abroad.io'
const PRIVACY_POLICY_URL = 'https://www.abroad.io/privacy-policy'
const TERMS_URL = 'https://www.abroad.io/terms-and-conditions'

// Must match the scopes requested by the server (abroad-server constants/zoom.js).
const ZOOM_SCOPES = [
  {
    scope: 'user:read:user',
    reason:
      'Read your Zoom user ID and email to identify the connected account and show it on the Zoom tab.',
  },
  {
    scope: 'meeting:write:meeting',
    reason: 'Create the Zoom meeting for a coaching plan.',
  },
  {
    scope: 'meeting:read:meeting',
    reason:
      'Receive meeting-started events so a meeting is matched to the correct session.',
  },
  {
    scope: 'meeting:delete:meeting',
    reason:
      "Delete the plan's Zoom meeting after its last scheduled session is cancelled.",
  },
  {
    scope: 'meeting:read:summary',
    reason:
      'Receive and retrieve the AI Companion meeting summary for coaching meetings you host.',
  },
]

const TROUBLESHOOTING_ITEMS = [
  {
    id: 'connection-cancelled',
    question: '"Zoom connection was cancelled."',
    answer: (
      <p className='mb-0'>
        The Zoom authorization page was closed or declined. Return to{' '}
        <strong>Scheduling → Zoom</strong> and select <strong>CONNECT</strong>{' '}
        again.
      </p>
    ),
  },
  {
    id: 'permissions-denied',
    question: '"Please accept all permissions and try again."',
    answer: (
      <p className='mb-0'>
        One or more permissions were not granted. Connect again and allow every
        permission listed on Zoom&apos;s authorization page. Abroad requires all
        of them.
      </p>
    ),
  },
  {
    id: 'link-expired',
    question: '"That link expired. Please connect again."',
    answer: (
      <p className='mb-0'>
        The authorization took too long or an old authorization page was reused.
        Start again from <strong>Scheduling → Zoom</strong>.
      </p>
    ),
  },
  {
    id: 'connection-failed',
    question:
      '"Could not complete the connection." or "Could not read your Zoom account."',
    answer: (
      <p className='mb-0'>
        Zoom did not complete the authorization. Try again. If the message keeps
        appearing, contact support.
      </p>
    ),
  },
  {
    id: 'wrong-account',
    question: 'The wrong Zoom account was connected',
    answer: (
      <p className='mb-0'>
        Select <strong>Disconnect</strong> on the Zoom tab, sign out of Zoom at
        zoom.us, then connect again and sign in with the correct host account.
      </p>
    ),
  },
  {
    id: 'no-zoom-link',
    question: 'A new session has a non-Zoom meeting link',
    answer: (
      <p className='mb-0'>
        Zoom is not connected, or Abroad&apos;s access was revoked or expired.
        Check <strong>Scheduling → Zoom</strong>. If no email is shown, connect
        again. Sessions booked while Zoom was disconnected keep the link they
        were created with.
      </p>
    ),
  },
  {
    id: 'no-summary',
    question: 'No AI summary appears in Session Insights',
    answer: (
      <ul className='mb-0 ps-3'>
        <li className='mb-2'>
          You hosted the meeting while signed in to the connected Zoom account.
        </li>
        <li className='mb-2'>
          AI Companion meeting summary was turned on during the meeting.
        </li>
        <li className='mb-2'>
          The meeting started no more than 45 minutes before the scheduled
          session time.
        </li>
        <li className='mb-2'>
          Zoom has finished generating the summary. Check the summaries list in
          your Zoom account.
        </li>
        <li>
          The meeting is the one Abroad created for the session. Summaries from
          other Zoom meetings are not added.
        </li>
      </ul>
    ),
  },
  {
    id: 'meeting-not-deleted',
    question: 'The Zoom meeting still exists after cancelling',
    answer: (
      <p className='mb-0'>
        Abroad deletes the meeting only after no scheduled sessions remain in
        the coaching plan. If Zoom was disconnected before the last session was
        cancelled, delete the meeting in your Zoom account.
      </p>
    ),
  },
]

const FAQ_ITEMS = [
  {
    id: 'recording',
    question: 'Does Abroad record my sessions?',
    answer: (
      <p className='mb-0'>
        No. Abroad does not record meetings or access recordings, transcripts,
        chat, audio, or video. It only receives the AI Companion meeting summary
        that Zoom generates when you turn it on.
      </p>
    ),
  },
  {
    id: 'client-visibility',
    question: 'Can my client see the AI summary?',
    answer: (
      <p className='mb-0'>
        No. The summary is added to confidential Session Insights. Your client
        does not see it.
      </p>
    ),
  },
  {
    id: 'who-can-connect',
    question: 'Who can connect Zoom?',
    answer: (
      <p className='mb-0'>
        Coach accounts, from <strong>Scheduling → Zoom</strong>. Each coach can
        connect one Zoom account.
      </p>
    ),
  },
  {
    id: 'meeting-per-session',
    question: 'Is a new Zoom meeting created for every session?',
    answer: (
      <p className='mb-0'>
        No. One recurring Zoom meeting is created per coaching plan, and every
        session in that plan uses the same join link.
      </p>
    ),
  },
  {
    id: 'paid-plan',
    question: 'Do I need a paid Zoom plan?',
    answer: (
      <p className='mb-0'>
        No. Zoom plan limits still apply, for example meeting length limits on
        free plans. AI Companion meeting summary availability depends on your
        Zoom plan.
      </p>
    ),
  },
  {
    id: 'legal-policies',
    question: "Where can I read Abroad's legal policies?",
    answer: (
      <p className='mb-0'>
        See the{' '}
        <a className='text-gold-light' href={PRIVACY_POLICY_URL}>
          Privacy Policy
        </a>{' '}
        and{' '}
        <a className='text-gold-light' href={TERMS_URL}>
          Terms and Conditions
        </a>
        .
      </p>
    ),
  },
]

const ADD_STEPS = [
  {
    title: 'Open the Zoom tab',
    body: (
      <>
        Sign in to Abroad as a coach and complete two-factor verification. Go to{' '}
        <strong>Scheduling → Zoom</strong>.
      </>
    ),
  },
  {
    title: 'Select CONNECT',
    body: <>Abroad sends you to Zoom&apos;s authorization page.</>,
  },
  {
    title: 'Sign in to Zoom',
    body: <>Use the Zoom account you host coaching sessions with.</>,
  },
  {
    title: 'Review and allow access',
    body: (
      <>
        Zoom lists the permissions described below. Abroad requires all of them;
        the connection is not completed if any are declined.
      </>
    ),
  },
  {
    title: 'Confirm the connection',
    body: (
      <>
        Zoom returns you to <strong>Scheduling → Zoom</strong>. Abroad shows
        &quot;Zoom account connected successfully.&quot; and your Zoom email
        appears in the table.
      </>
    ),
  },
]

const SUMMARY_STEPS = [
  <>
    Start the session&apos;s Zoom meeting as host, no more than 45 minutes
    before the scheduled start time.
  </>,
  <>Turn on AI Companion meeting summary in Zoom and hold the session.</>,
  <>
    End the meeting. When Zoom finishes the summary, usually within a few
    minutes, Abroad adds it to the session under{' '}
    <strong>Clients → [client] → Sessions → [session]</strong>, in{' '}
    <strong>Session Insights (Confidential)</strong>, headed &quot;Zoom AI
    summary&quot;. If the session record does not exist yet, Abroad creates it.
  </>,
]

const SectionLabel = ({ children }) => (
  <div className='text-gold-light text-uppercase font-montserrat small-title-2 fw-bold tracking-widest mb-3'>
    {children}
  </div>
)

const SubHeading = ({ children }) => (
  <h3 className='font-heading sub-title-2 text-rich-navy mb-3'>{children}</h3>
)

/**
 * Public Zoom Marketplace documentation page.
 * Covers adding, using, troubleshooting, and removing Abroad's Zoom integration.
 * Content must stay in sync with the implemented integration (scopes, flows, data handling).
 */
const ZoomDocumentation = () => {
  return (
    <ZoomResourceLayout
      title='Zoom Integration Guide'
      description="How to add, use, troubleshoot, and remove Abroad's Zoom integration."
      path='/zoom/documentation'
      eyebrow='Integration guide'
      heading='Using Abroad with Zoom'
      intro="Abroad coaches can connect their own Zoom account so coaching sessions booked in Abroad automatically receive a Zoom meeting link, and so Zoom AI Companion meeting summaries are added to the coach's confidential session notes.">
      <div className='zoom-resource-sections'>
        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift'
          aria-labelledby='overview'>
          <SectionLabel>Overview</SectionLabel>
          <h2
            id='overview'
            className='font-heading sub-title text-rich-navy mb-4'>
            What the integration does
          </h2>
          <ul className='zoom-resource-check-list list-unstyled mb-4'>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              Creates one recurring Zoom meeting in the coach&apos;s Zoom
              account for each coaching plan, with the waiting room on and join
              before host off.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              Adds the Zoom join link to the calendar invitation for each
              session booked in Abroad.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              Deletes that Zoom meeting when the last scheduled session in the
              plan is cancelled.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              After a session, adds the Zoom AI Companion meeting summary to the
              coach&apos;s confidential Session Insights for that session.
            </li>
          </ul>
          <div className='zoom-resource-note font-paragraph small-paragraph text-slate-gray'>
            <strong className='text-rich-navy'>What it does not do.</strong>{' '}
            Abroad does not record meetings, access recordings or transcripts,
            join meetings, or process audio or video. Meetings run entirely in
            Zoom.
          </div>
        </section>

        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift'
          aria-labelledby='prerequisites'>
          <SectionLabel>Before you start</SectionLabel>
          <h2
            id='prerequisites'
            className='font-heading sub-title text-rich-navy mb-4'>
            Prerequisites
          </h2>
          <ul className='zoom-resource-check-list list-unstyled mb-4'>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              An Abroad coach account with two-factor authentication set up.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              A Zoom account. If your Zoom account is managed by an
              organization, your Zoom administrator may need to allow Abroad in
              the Zoom App Marketplace.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              Your Abroad calendar connected under{' '}
              <strong>Scheduling → Calendar</strong>. Booking is unavailable
              without it.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              A time zone and a Video Call Link saved under{' '}
              <strong>Scheduling → Settings</strong>. The Video Call Link is
              used when a Zoom meeting cannot be created.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              For AI meeting summaries: AI Companion meeting summary available
              on your Zoom plan and turned on for the meeting.
            </li>
          </ul>
          <div className='zoom-resource-note font-paragraph small-paragraph text-slate-gray'>
            <strong className='text-rich-navy'>
              Zoom plan limits still apply.
            </strong>{' '}
            For example, meetings on free Zoom plans are limited to 40 minutes.
          </div>
        </section>

        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift'
          aria-labelledby='add-app'>
          <SectionLabel>Adding the app</SectionLabel>
          <h2
            id='add-app'
            className='font-heading sub-title text-rich-navy mb-4'>
            Connect your Zoom account
          </h2>
          <ol className='zoom-resource-steps list-unstyled mb-4'>
            {ADD_STEPS.map((step, index) => (
              <li key={step.title}>
                <span
                  className='zoom-resource-steps__number'
                  aria-hidden='true'>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className='font-heading sub-title-2 text-rich-navy mb-2'>
                    {step.title}
                  </h3>
                  <p className='font-paragraph small-paragraph text-slate-gray mb-0'>
                    {step.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <SubHeading>Permissions Abroad requests</SubHeading>
          <dl className='zoom-resource-facts mb-4'>
            {ZOOM_SCOPES.map((item) => (
              <div key={item.scope} className='mb-3'>
                <dt className='text-rich-navy font-paragraph small-paragraph-bold mb-1'>
                  <code>{item.scope}</code>
                </dt>
                <dd className='font-paragraph small-paragraph text-slate-gray mb-0'>
                  {item.reason}
                </dd>
              </div>
            ))}
          </dl>

          <p className='font-paragraph small-paragraph text-slate-gray mb-0'>
            If the connection does not complete, see{' '}
            <a className='text-gold-light' href='#troubleshooting'>
              Troubleshooting
            </a>
            .
          </p>
        </section>

        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift'
          aria-labelledby='use-app'>
          <SectionLabel>Usage</SectionLabel>
          <h2
            id='use-app'
            className='font-heading sub-title text-rich-navy mb-4'>
            Using Zoom for coaching sessions
          </h2>

          <SubHeading>Scheduling sessions</SubHeading>
          <ul className='font-paragraph small-paragraph text-slate-gray mb-4 ps-3'>
            <li className='mb-2'>
              When your client books a session in Abroad, or you schedule one
              from <strong>Scheduling → Booking</strong>, Abroad creates a Zoom
              meeting in your connected account named &quot;Coaching: [your
              first name] &amp; [client first name]&quot;.
            </li>
            <li className='mb-2'>
              Every session in the same coaching plan uses that meeting and join
              link.
            </li>
            <li className='mb-2'>
              The meeting has the waiting room on, and participants cannot join
              before the host.
            </li>
            <li>
              The join link is added to the calendar invitation, as &quot;Video
              Call link&quot;, that you and your client receive.
            </li>
          </ul>

          <SubHeading>Joining a session</SubHeading>
          <ul className='font-paragraph small-paragraph text-slate-gray mb-4 ps-3'>
            <li className='mb-2'>
              Open the Zoom link in the calendar invitation. The link opens Zoom
              directly.
            </li>
            <li>
              Start the meeting while signed in to the connected Zoom account so
              you are the host. Your client waits in the waiting room until you
              admit them.
            </li>
          </ul>

          <SubHeading>Rescheduling and cancelling</SubHeading>
          <ul className='font-paragraph small-paragraph text-slate-gray mb-4 ps-3'>
            <li className='mb-2'>
              Rescheduling updates the calendar invitation time and keeps the
              same Zoom link. No new meeting is created.
            </li>
            <li className='mb-2'>
              Cancelling removes the session. When the last scheduled session in
              a coaching plan is cancelled, Abroad deletes the Zoom meeting from
              your Zoom account.
            </li>
            <li>
              Sessions cannot be cancelled in Abroad within 24 hours of their
              start time.
            </li>
          </ul>

          <SubHeading>AI meeting summaries</SubHeading>
          <ol className='font-paragraph small-paragraph text-slate-gray mb-3'>
            {SUMMARY_STEPS.map((step, index) => (
              <li key={index} className='mb-2'>
                {step}
              </li>
            ))}
          </ol>
          <ul className='font-paragraph small-paragraph text-slate-gray mb-4 ps-3'>
            <li className='mb-2'>
              The summary is added to confidential Session Insights. Your client
              does not see it.
            </li>
            <li>
              If you then edit the session and add Post-Session Practices, your
              client receives the usual email inviting them to view them in
              Abroad.
            </li>
          </ul>

          <div className='zoom-resource-note font-paragraph small-paragraph text-slate-gray'>
            <strong className='text-rich-navy'>
              If Zoom is not connected or a meeting cannot be created,
            </strong>{' '}
            scheduling still works. A coaching plan that does not yet have a
            Zoom meeting uses the Video Call Link saved under{' '}
            <strong>Scheduling → Settings</strong> instead of a Zoom link. A
            plan that already has a Zoom meeting keeps using that meeting&apos;s
            link.
          </div>
        </section>

        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift'
          aria-labelledby='troubleshooting'>
          <SectionLabel>Troubleshooting</SectionLabel>
          <h2
            id='troubleshooting'
            className='font-heading sub-title text-rich-navy mb-4'>
            Common issues
          </h2>
          <div className='accordion'>
            {TROUBLESHOOTING_ITEMS.map((item) => (
              <ZoomAccordionItem
                key={item.id}
                id={item.id}
                question={item.question}>
                {item.answer}
              </ZoomAccordionItem>
            ))}
          </div>
        </section>

        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift'
          aria-labelledby='remove-app'>
          <SectionLabel>Removing the app</SectionLabel>
          <h2
            id='remove-app'
            className='font-heading sub-title text-rich-navy mb-4'>
            Remove Abroad's Zoom integration
          </h2>

          <SubHeading>Option 1: Disconnect in Abroad</SubHeading>
          <ol className='font-paragraph small-paragraph text-slate-gray mb-4'>
            <li className='mb-2'>Sign in to Abroad as a coach.</li>
            <li className='mb-2'>
              Go to <strong>Scheduling → Zoom</strong>.
            </li>
            <li className='mb-2'>
              Select <strong>Disconnect</strong> next to your Zoom email and
              confirm.
            </li>
            <li>The tab shows &quot;No accounts connected yet.&quot;</li>
          </ol>

          <SubHeading>
            Option 2: Remove from the Zoom App Marketplace
          </SubHeading>
          <ol className='font-paragraph small-paragraph text-slate-gray mb-4'>
            <li className='mb-2'>
              Sign in to your Zoom account and navigate to the Zoom App
              Marketplace.
            </li>
            <li className='mb-2'>
              Click <strong>Manage → Added Apps</strong>, or search for Abroad.
            </li>
            <li className='mb-2'>Select the Abroad app.</li>
            <li>
              Click <strong>Remove</strong> and confirm.
            </li>
          </ol>

          <div className='zoom-resource-note'>
            <p className='text-gold-light text-uppercase font-montserrat small-title fw-bold tracking-widest mb-3'>
              What happens after removal
            </p>
            <ul className='font-paragraph small-paragraph text-slate-gray mb-0 ps-3'>
              <li className='mb-2'>
                Disconnecting in Abroad revokes Abroad&apos;s Zoom access token.
                Removing the app in Zoom sends Abroad a deauthorization
                notification. In both cases Abroad deletes the stored Zoom
                connection: your Zoom user ID, email, and encrypted access and
                refresh tokens.
              </li>
              <li className='mb-2'>
                Abroad stops creating and deleting Zoom meetings and stops
                adding meeting summaries.
              </li>
              <li className='mb-2'>
                Zoom meetings already created remain in your Zoom account, where
                you can manage or delete them. Existing calendar invitations
                keep their Zoom links.
              </li>
              <li className='mb-2'>
                New coaching plans use your saved Video Call Link until you
                connect Zoom again. Plans that already have a Zoom meeting keep
                using that meeting&apos;s link.
              </li>
              <li>
                Session records, including meeting IDs, join links, and
                summaries already added to Session Insights, remain in Abroad
                under Abroad&apos;s retention practices. To request deletion,
                email{' '}
                <a className='text-gold-light' href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
                .
              </li>
            </ul>
          </div>
        </section>

        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift'
          aria-labelledby='data-handling'>
          <SectionLabel>Data handling</SectionLabel>
          <h2
            id='data-handling'
            className='font-heading sub-title text-rich-navy mb-4'>
            Data and security
          </h2>
          <dl className='zoom-resource-facts mb-4'>
            <div className='mb-4'>
              <dt className='text-gold-light text-uppercase font-montserrat small-title fw-bold tracking-widest mb-2'>
                Stored by Abroad
              </dt>
              <dd className='font-paragraph small-paragraph text-slate-gray mb-0'>
                Zoom user ID and email; OAuth access and refresh tokens
                (encrypted) and their expiry; the Zoom meeting ID and join link
                for each session; AI Companion summary text, sanitized and
                stored encrypted in Session Insights, with a link to the summary
                in Zoom and the meeting identifier used to prevent duplicates.
              </dd>
            </div>
            <div className='mb-4'>
              <dt className='text-gold-light text-uppercase font-montserrat small-title fw-bold tracking-widest mb-2'>
                Not stored
              </dt>
              <dd className='font-paragraph small-paragraph text-slate-gray mb-0'>
                Recordings, transcripts, chat messages, audio, or video.
              </dd>
            </div>
            <div>
              <dt className='text-gold-light text-uppercase font-montserrat small-title fw-bold tracking-widest mb-2'>
                Security
              </dt>
              <dd className='font-paragraph small-paragraph text-slate-gray mb-0'>
                Zoom credentials and tokens stay on Abroad&apos;s servers and
                are never sent to the browser. Zoom event notifications are
                verified with Zoom&apos;s HMAC SHA-256 signature and rejected if
                invalid or older than 5 minutes.
              </dd>
            </div>
          </dl>
          <p className='font-paragraph small-paragraph text-slate-gray mb-0'>
            Read the{' '}
            <a className='text-gold-light' href={PRIVACY_POLICY_URL}>
              Abroad Privacy Policy
            </a>{' '}
            or email{' '}
            <a className='text-gold-light' href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>{' '}
            to access or delete your data.
          </p>
        </section>

        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift'
          aria-labelledby='faq'>
          <SectionLabel>FAQ</SectionLabel>
          <h2 id='faq' className='font-heading sub-title text-rich-navy mb-4'>
            Frequently asked questions
          </h2>
          <div className='accordion'>
            {FAQ_ITEMS.map((item) => (
              <ZoomAccordionItem
                key={item.id}
                id={item.id}
                question={item.question}>
                {item.answer}
              </ZoomAccordionItem>
            ))}
          </div>
        </section>

        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift text-center'
          aria-labelledby='more-help'>
          <SectionLabel>Contact support</SectionLabel>
          <h2
            id='more-help'
            className='font-heading sub-title text-rich-navy mb-3'>
            Still need help?
          </h2>
          <p className='font-paragraph small-paragraph text-slate-gray mb-4 mx-auto zoom-resource-closing-copy'>
            The Abroad support team helps with connecting Zoom, meeting links,
            AI summaries, and removal. Monday–Friday, 9:00 AM–5:00 PM Eastern
            Time, excluding US holidays, with a first response within 24 hours
            on operating days.
          </p>
          <Link
            className='btn btn-gold text-white text-uppercase prosperity-button-cta font-paragraph small-paragraph-bold'
            to='/zoom/support'>
            View support options
          </Link>
        </section>
      </div>
    </ZoomResourceLayout>
  )
}

export default ZoomDocumentation
